<template>
  <div class="login-wrapper">
    <div class="login-left">
      <h1 class="main-title">Welcome back to<br>Clementine Analytics</h1>
      <p class="main-subtitle">Enter your email to receive a password recovery link</p>
    </div>

    <div class="login-right">
      <div class="login-right-content">
        <div class="login-header-text">
          <h2>Forgot Password</h2>
          <p>We'll help you get back into your account</p>
        </div>

        <form @submit="onSubmit" class="w-100">
          <div class="row" v-if="isSent">
            <div class="col-12">
              <div class="alert alert-success alert-dismissible custom-alert">
                A recovery email has been sent to <strong>{{ email }}</strong>
              </div>
            </div>
          </div>

          <div class="form-floating-custom">
            <i class="fas fa-envelope input-icon"></i>
            <input name="email" id="forgetEmail" placeholder=" " type="email" class="form-control" v-model="email"
              required autocomplete="email" />
            <label for="forgetEmail">Email Address</label>
          </div>

          <div class="btn-container">
            <button v-if="!isSendEmailLoader" type="submit" class="btn-login-main">
              {{ isSent ? 'Resend Recovery Link' : 'Send Recovery Link' }}
            </button>
            <button v-else class="btn-login-main" type="button" disabled>
              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Sending...
            </button>

            <button class="btn-back-login" type="button" @click="$router.push('/login')">
              <i class="fas fa-arrow-left me-2"></i>Return to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import agent, { masterServiceBaseURL } from '@/services/agent';
import { defineComponent } from 'vue';
import { ForgetPasswordResponse } from './ForgetPassword.model';
import authService from '@/services/authService';
import router from '@/router/router';
import { API_KEY } from './Constants';

export default defineComponent({
  created() {
    this.classAddRemove();
    agent.setBaseUrl(masterServiceBaseURL);
  },
  data() {
    return {
      email: null as string | null,
      isSendEmailLoader: false,
      isSent: false
    }
  },
  mounted() {
    this.email = null;
    this.RedirectTo();
  },
  methods: {
    async onSubmit(event: any) {
      event.preventDefault();
      let post = { email: this.email };
      this.isSendEmailLoader = true;
      this.isSent = false;

      try {
        const response = await agent.postAnonymous<ForgetPasswordResponse>(API_KEY.POST, post);
        if (response && response.result) {
          this.isSent = true;
        }
      } finally {
        this.isSendEmailLoader = false;
      }
    },
    RedirectTo() {
      const isExpired = authService.isExpired(authService.getToken());
      if (!isExpired) router.push("/");
    },
    classAddRemove() {
      var bodyElement = document.body;
      bodyElement.classList.remove('sidebar-mini', 'layout-fixed', 'register-page');
    }
  }
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.login-wrapper {
  height: 100vh;
  display: flex;
  background-color: #fbfcfd;
  font-family: 'Plus Jakarta Sans', sans-serif;
  overflow: hidden;
}

.login-left {
  width: 45%;
  background: linear-gradient(135deg, #0a3370 0%, #03607A 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 5rem;
  color: #ffffff;
  position: relative;
  overflow: hidden;
}

.login-left::before {
  content: "";
  position: absolute;
  top: -10%;
  right: -10%;
  width: 70%;
  height: 70%;
  background: radial-gradient(circle, rgba(73, 199, 235, 0.3) 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(80px);
  z-index: 0;
}

.main-title {
  font-size: 3.25rem;
  font-weight: 700;
  letter-spacing: -1.5px;
  line-height: 1.2;
  text-align: left;
  z-index: 1;
  color: #ffffff;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin: 0;
}

.main-subtitle {
  font-size: 1.15rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  text-align: left;
  margin-top: 2rem;
  z-index: 1;
  max-width: 90%;
  line-height: 1.6;
}

.login-right {
  width: 55%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #ffffff;
}

.login-right-content {
  width: 100%;
  max-width: 440px;
  padding: 3rem;
  animation: slideIn 0.8s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.login-header-text {
  margin-bottom: 2.5rem;
  text-align: center;
}

.login-header-text h2 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.login-header-text p {
  color: #64748b;
  font-size: 0.95rem;
}

.form-floating-custom {
  position: relative;
  margin-bottom: 2rem;
}

.form-floating-custom .form-control {
  height: 60px;
  padding: 1.6rem 1rem 0.6rem 3.25rem;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: #f8fafc;
  width: 100%;
  color: #1e293b;
}

.form-floating-custom .input-icon {
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 1.1rem;
  z-index: 2;
  transition: all 0.2s ease;
}

.form-floating-custom .form-control:focus~.input-icon {
  color: #03607A;
}

.form-floating-custom .form-control:focus {
  border-color: #03607A;
  background-color: #fff;
  box-shadow: 0 0 0 4px rgba(3, 96, 122, 0.1);
  outline: none;
}

.form-floating-custom label {
  position: absolute;
  top: 0;
  left: 3.25rem;
  padding: 1.1rem 0;
  pointer-events: none;
  transition: all 0.2s ease;
  color: #94a3b8;
  font-size: 1rem;
}

.form-floating-custom .form-control:focus~label,
.form-floating-custom .form-control:not(:placeholder-shown)~label {
  transform: translateY(-10px) scale(0.8) translate(-15%, 0);
  color: #03607A;
  font-weight: 700;
  background: transparent;
}

.btn-login-main {
  height: 56px;
  width: 100%;
  border-radius: 100px;
  background: #03607A;
  color: #ffffff;
  font-weight: 700;
  font-size: 1.1rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(3, 96, 122, 0.25);
  margin-bottom: 1.5rem;
}

.btn-login-main:hover {
  background: #024a5f;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(3, 96, 122, 0.35);
}

.btn-back-login {
  width: 100%;
  background: transparent;
  border: none;
  color: #64748b;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-back-login:hover {
  color: #03607A;
}

.custom-alert {
  border-radius: 12px;
  border: none;
  background-color: #ecfdf5;
  color: #065f46;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
}

@media screen and (max-width: 991px) {
  .login-left {
    display: none;
  }

  .login-right {
    width: 100%;
  }
}
</style>
